import React from 'react';
import styles from './RightPanel.module.scss';
import SuggestionsPanel from '../SuggestionsPanel/SuggestionsPanel';
import ReasoningPanel from '../ReasoningPanel/ReasoningPanel';
import Library from '../Library/Library';

import { SuggestedQuestion } from '../../../../../../../../store/dashboard/askAica/actions';
import CrossIcon from '../../../../../../../../assets/Svg/CrossIcon';
import { AskAicaFileRef } from '../../hooks/useAskAicaFileActions';
import { ASK_AICA_SURFACE, AskAicaSurface } from '../../context/AskAicaSurfaceContext';

interface RightPanelProps {
    mode: 'suggestions' | 'reasoning';
    onModeChange: (mode: 'suggestions' | 'reasoning') => void;
    onSuggestionClick?: (suggestion: SuggestedQuestion) => void;
    onCrossButtonClick?: () => void;
    /** Opens a generated file in the matching in-app viewer (docx/xlsx/pdf). */
    onOpenFile: (file: AskAicaFileRef) => void;
    /** Snapshots a generated file into Reports (docx/xlsx/txt). Absent when the reports.addToReports visibility key is off. */
    onAddFileToReports?: (file: AskAicaFileRef) => void;
    /** Portfolio has no Library equivalent — it reads the leads-only
     * askAicaReducer slice directly (RECUR-27369), which the portfolio surface
     * never populates. Defaulting to 'leads' keeps existing callers unchanged;
     * 'portfolio' hides Library so a leads session's stale slice data can never
     * bleed into the portfolio panel. */
    surface?: AskAicaSurface;
}

const RightPanel: React.FC<RightPanelProps> = ({
    mode,
    onModeChange,
    onSuggestionClick,
    onCrossButtonClick,
    onOpenFile,
    onAddFileToReports,
    surface = ASK_AICA_SURFACE.LEADS,
}) => {
    const isPortfolio = surface === ASK_AICA_SURFACE.PORTFOLIO;

    return (
        <div className={styles.RightPanel}>
            {/* Toggle Header */}
            <div className={styles.RightPanelHeader}>
                {isPortfolio ? (
                    <div className={styles.ToggleButtons}>
                        <button className={`${styles.ToggleButton} ${styles.Active}`}>Reasoning</button>
                    </div>
                ) : (
                    <div className={styles.ToggleButtons}>
                        <button
                            className={`${styles.ToggleButton} ${mode === 'suggestions' ? styles.Active : ''}`}
                            onClick={() => onModeChange('suggestions')}
                        >
                            Suggestions
                        </button>
                        <button
                            className={`${styles.ToggleButton} ${mode === 'reasoning' ? styles.Active : ''}`}
                            onClick={() => onModeChange('reasoning')}
                        >
                            Reasoning
                        </button>
                    </div>
                )}
                <button
                    type="button"
                    className={styles.CrossButton}
                    onClick={onCrossButtonClick}
                    aria-label="Close Ask AICA"
                    data-testid="ask-aica-close-button"
                >
                    <CrossIcon colour="#000000" />
                </button>
            </div>

            {/* Panel Content */}
            <div className={styles.RightPanelContent}>
                {isPortfolio ? (
                    <ReasoningPanel />
                ) : mode === 'suggestions' ? (
                    <SuggestionsPanel onSuggestionClick={onSuggestionClick} />
                ) : (
                    <ReasoningPanel />
                )}
            </div>

            {/* Library — leads only (see surface prop doc above) */}
            {!isPortfolio && <Library onOpenFile={onOpenFile} onAddFileToReports={onAddFileToReports} />}
        </div>
    );
};

export default RightPanel;
